import { cn } from "../cn";

// Who else is on the same thing (ADR-279): a few round initials, the rest counted, the full names on hover and for
// screen readers. The screen gives the people as the room lets this user see them: a company alone is one of them.

export interface PresencePerson {
  key: string;
  /** A person's name, or a company's when the user may not know who of it is there. */
  name: string;
  /** The company of a person; null when `name` already is the company. */
  organization: string | null;
  /** An assistant working for someone (ADR-300 §7): `name` is the assistant, this the person who delegated it. */
  delegatedBy?: string | undefined;
}

interface PresenceStackProps {
  people: readonly PresencePerson[];
  /** People beyond `people`, counted. */
  more?: number;
  /** What the group is, in the words of the screen ("Also here"). */
  label: string;
  /** `sm` next to a title, `xs` inside a row of a grid. */
  size?: "sm" | "xs";
  /** Initials shown before the rest is counted. */
  max?: number;
  /** What the count says to a screen reader ("3 more"). */
  moreLabel?: (count: number) => string;
  className?: string;
}

/** The name and company of a person, or the assistant and who it works for, as the hover and the screen reader say it. */
export const presenceName = (person: PresencePerson): string => {
  if (person.delegatedBy) return `${person.name} (for ${person.delegatedBy})`;
  return person.organization ? `${person.name} (${person.organization})` : person.name;
};

/** The initials of a name: the first letter of its first two words. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((word) => [...word][0]?.toLocaleUpperCase() ?? "")
    .join("");
}

export function PresenceStack({
  people,
  more = 0,
  label,
  size = "sm",
  max = 3,
  moreLabel = (count) => `+${count}`,
  className,
}: PresenceStackProps) {
  if (people.length === 0) return null;
  const shown = people.slice(0, max);
  const counted = people.length - shown.length + more;
  const bubble = cn(
    "inline-flex shrink-0 items-center justify-center rounded-full border border-border bg-muted font-medium text-foreground ring-2 ring-background",
    size === "sm" ? "size-7 text-xs" : "size-5 text-[0.625rem]",
  );
  return (
    <ul aria-label={label} className={cn("flex items-center -space-x-1.5", className)}>
      {shown.map((person) => (
        <li key={person.key} className={bubble} title={presenceName(person)}>
          <span aria-hidden>{initialsOf(person.name)}</span>
          <span className="sr-only">{presenceName(person)}</span>
        </li>
      ))}
      {counted > 0 && (
        <li className={bubble} title={people.slice(max).map(presenceName).join(", ") || undefined}>
          <span aria-hidden>+{counted}</span>
          <span className="sr-only">{moreLabel(counted)}</span>
        </li>
      )}
    </ul>
  );
}
